import { mergeRegister } from "@lexical/utils";
import type { PopperProps } from "@mui/material";
import {
  $getSelection,
  COMMAND_PRIORITY_LOW,
  getDOMSelection,
  type LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { $isDesignHuddleNode } from "../../nodes/design-huddle-node";
import { $isImageNode } from "../../nodes/image-node";
import { $isPollNode } from "../../nodes/poll/helpers";
import { $isSurveyLinkNode } from "../../nodes/survey-link-node";
import { $isVideoNode } from "../../nodes/video-node";

export function useGenericFloatingToolbar({
  editor,
  editorScrollRef,
  channel,
}: {
  editor: LexicalEditor;
  editorScrollRef?: RefObject<HTMLDivElement | null>;
  channel: "slack" | "email" | "teams" | "text";
}): {
  open: boolean;
  anchorEl: PopperProps["anchorEl"] | null;
} {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<PopperProps["anchorEl"]>(null);
  const previousAnchorElPosition = useRef<DOMRect>(null);

  useEffect(() => {
    if (anchorEl) {
      if (typeof anchorEl === "object") {
        previousAnchorElPosition.current = anchorEl.getBoundingClientRect();
      } else {
        previousAnchorElPosition.current = anchorEl().getBoundingClientRect();
      }
    }
  }, [anchorEl]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    const selection = $getSelection();
    const nativeSelection = getDOMSelection(editor._window);
    const rootElement = editor.getRootElement();

    const node = selection?.getNodes()?.[0];

    const isVideoWithTitle =
      channel === "slack" &&
      ($isVideoNode(node) || ($isDesignHuddleNode(node) && node.__contentType === "video"));

    if (isVideoWithTitle) {
      const domElement = editor.getElementByKey(node.getKey());
      setAnchorEl(domElement);
      setOpen(true);
      return;
    }

    if ($isImageNode(node) || $isDesignHuddleNode(node) || $isVideoNode(node)) {
      const domElement = editor.getElementByKey(node.getKey());
      if (domElement) {
        // Traverse down to the nested img element
        const imgElement = domElement.querySelector("img");
        if (imgElement) {
          setAnchorEl(imgElement);
          setOpen(true);
        }
        return;
      }
    }

    if ($isPollNode(node)) {
      const domElement = editor.getElementByKey(node.getKey());
      setAnchorEl(domElement);
      setOpen(true);
      return;
    }

    if ($isSurveyLinkNode(node?.getParent())) {
      const surveyLinkNode = node.getParent();
      if (surveyLinkNode) {
        const domElement = editor.getElementByKey(surveyLinkNode.getKey());
        setAnchorEl(domElement);
        setOpen(true);
        return;
      }
    }

    if (
      selection === null ||
      nativeSelection === null ||
      nativeSelection.isCollapsed ||
      rootElement === null ||
      !rootElement.contains(nativeSelection.anchorNode) ||
      nativeSelection.anchorOffset === nativeSelection.focusOffset
    ) {
      handleClose();
      return;
    }

    const getBoundingClientRect = (): DOMRect => {
      if (nativeSelection.rangeCount === 0 && previousAnchorElPosition.current) {
        setOpen(false);
        return previousAnchorElPosition.current;
      }
      return nativeSelection.getRangeAt(0).getBoundingClientRect();
    };

    setOpen(true);

    setAnchorEl({ getBoundingClientRect });
  }, [handleClose, editor, channel]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(handleMouseUp);
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          handleMouseUp();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, handleMouseUp]);

  useEffect(() => {
    // When an image is selected, we don't get the subsequent selection change events
    // that selecting two chars would give us, so we need to trigger it manually
    editor.dispatchCommand(SELECTION_CHANGE_COMMAND, undefined);
  }, [editor]);

  useEffect(() => {
    const scrollerElem = editorScrollRef?.current;

    const update = (): void => {
      editor.getEditorState().read(() => {
        handleMouseUp();
      });
    };

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update);
    if (scrollerElem) {
      scrollerElem.addEventListener("scroll", update);
    }

    return () => {
      window.removeEventListener("resize", update);
      window.addEventListener("scroll", update);
      if (scrollerElem) {
        scrollerElem.removeEventListener("scroll", update);
      }
    };
  }, [editor, handleMouseUp, editorScrollRef]);

  return { open, anchorEl };
}
